import { type ComponentProps, type FC, Fragment, type ReactNode } from 'react';

import { Box, Bleed, Divider, Stack, Text } from '@eneco/sparky';

import { ExpandableRowLayout } from './PriceDetails.Content';
import { DetailValueRow } from './PriceDetails.DetailValueRow';
import { InfoPopover } from './PriceDetails.InfoPopover';
import {
  type Details,
  type FormattedValue,
  type TariffCalculationRowItem,
  type TariffCalculationRowProps,
  type TariffCalculationSection,
  type TariffExpandableRowProps,
  type TariffExpandableSection,
  type TariffSectionBase,
} from './PriceDetails.types';
import { resolveTypeIcon, useEuroFormatters } from './PriceDetails.utils';

const toCalculationRowItem = (section: TariffCalculationSection): TariffCalculationRowItem => ({
  quantity: section.quantity ?? '',
  unitPrice: section.unitPrice ?? 0,
  unitLabel: section.unitLabel ?? '',
  total: section.total ?? 0,
});

const SectionDivider: FC = () => (
  <Bleed horizontal="3">
    <Divider weight="heavy" />
  </Bleed>
);

const TariffSectionHeader: FC<Pick<TariffCalculationSection, 'title' | 'infoPopoverContent'>> = ({
  title,
  infoPopoverContent,
}) => (
  <Stack direction="row" alignY="center" gap="2">
    <Text size={{ initial: 'BodyS', md: 'BodyM' }} weight="bold">
      {title}
    </Text>
    {infoPopoverContent && <InfoPopover title={title} content={infoPopoverContent} />}
  </Stack>
);

interface TariffSectionCardProps {
  icon?: ReactNode;
  title: string;
  infoPopoverContent?: string;
  children: ReactNode;
  gap?: ComponentProps<typeof Stack>['gap'];
}

const TariffSectionCard: FC<TariffSectionCardProps> = ({ icon, title, infoPopoverContent, children, gap = '3' }) => (
  <Stack direction="row" alignY="start" gap={gap}>
    {icon && <Box>{icon}</Box>}
    <Stack.Item grow={true}>
      <Stack gap="1">
        <TariffSectionHeader title={title} infoPopoverContent={infoPopoverContent} />
        {children}
      </Stack>
    </Stack.Item>
  </Stack>
);

interface TariffCalculationRowsProps {
  sectionTitle: string;
  calculationRows: TariffCalculationRowItem[];
  formatEuro: (value: FormattedValue) => string;
  formatEuroHighPrecision: (value: FormattedValue) => string;
}

const TariffCalculationRows: FC<TariffCalculationRowsProps> = ({
  sectionTitle,
  calculationRows,
  formatEuro,
  formatEuroHighPrecision,
}) => (
  <Stack gap="3">
    {calculationRows.map((calculationRow, calculationRowIndex) => {
      const isSimpleRow = calculationRow.variant === 'simple';

      return (
        <Box key={`${sectionTitle}-calculation-${calculationRowIndex}`}>
          {isSimpleRow ? (
            <DetailValueRow
              text={calculationRow.text ?? String(calculationRow.quantity)}
              value={calculationRow.value}
              formatValue={formatEuro}
              textColor="textSecondary"
            />
          ) : (
            <Stack gap="1">
              <DetailValueRow
                text={calculationRow.text ?? String(calculationRow.quantity)}
                formatValue={formatEuro}
                textSize="BodyXS"
                textColor="textLowEmphasis"
              />
              <DetailValueRow
                text={`${formatEuroHighPrecision(calculationRow.unitPrice)} × ${calculationRow.quantity} =`}
                value={calculationRow.total}
                formatValue={formatEuro}
                textColor="textSecondary"
              />
            </Stack>
          )}
        </Box>
      );
    })}
  </Stack>
);

interface TariffExpandableDetailsProps {
  details?: Details[];
  formatEuro: (value: FormattedValue) => string;
}

const TariffExpandableDetails: FC<TariffExpandableDetailsProps> = ({ details, formatEuro }) => (
  <Stack gap="2">
    {details?.map(({ text, value, highlightText }, index) => (
      <Box key={index}>
        <DetailValueRow text={text} value={value} highlightText={highlightText} formatValue={formatEuro} />
      </Box>
    ))}
  </Stack>
);

/** Shared frame for tariff section lists (expandable summary + padded stack). */
const TariffRowSectionsShell: FC<{
  summary?: Details;
  stackGap: ComponentProps<typeof Stack>['gap'];
  children: ReactNode;
}> = ({ summary, stackGap, children }) => (
  <ExpandableRowLayout summary={summary}>
    <Box padding="3">
      <Stack gap={stackGap}>{children}</Stack>
    </Box>
  </ExpandableRowLayout>
);

interface TariffSectionListProps<T extends TariffSectionBase & { title: string }> {
  summary?: Details;
  sections: T[];
  showSectionDivider?: boolean;
  stackGap: ComponentProps<typeof Stack>['gap'];
  renderSection: (section: T, sectionIndex: number) => ReactNode;
}

const TariffSectionList = <T extends TariffSectionBase & { title: string }>({
  summary,
  sections,
  showSectionDivider = false,
  stackGap,
  renderSection,
}: TariffSectionListProps<T>) => (
  <TariffRowSectionsShell summary={summary} stackGap={stackGap}>
    {sections.map((section, sectionIndex) => (
      <Fragment key={`${section.title}-${sectionIndex}`}>
        {renderSection(section, sectionIndex)}
        {showSectionDivider && sections.length >= 2 && sectionIndex < sections.length - 1 && <SectionDivider />}
      </Fragment>
    ))}
  </TariffRowSectionsShell>
);

export const TariffCalculationRow: FC<TariffCalculationRowProps> = ({
  type,
  icon,
  infoPopoverContent,
  unitPrice,
  unitLabel,
  quantity,
  total,
  summary,
  sections,
  showSectionDivider = false,
  isCompact = false,
  gap = '3',
}) => {
  const { formatEuro, formatEuroHighPrecision } = useEuroFormatters();

  const resolvedSections: TariffCalculationSection[] = sections?.length
    ? sections
    : [
        {
          type,
          icon,
          title: '',
          infoPopoverContent,
          unitPrice: unitPrice ?? 0,
          unitLabel: unitLabel ?? '',
          quantity: quantity ?? '',
          total: total ?? 0,
        },
      ];

  return (
    <TariffSectionList
      summary={summary}
      sections={resolvedSections}
      showSectionDivider={showSectionDivider}
      stackGap={isCompact ? 0 : gap}
      renderSection={section => (
        <Box>
          <TariffSectionCard
            icon={resolveTypeIcon(section.type, section.icon)}
            title={section.title}
            infoPopoverContent={section.infoPopoverContent}>
            <TariffCalculationRows
              sectionTitle={section.title}
              calculationRows={
                section.calculationRows?.length ? section.calculationRows : [toCalculationRowItem(section)]
              }
              formatEuro={formatEuro}
              formatEuroHighPrecision={formatEuroHighPrecision}
            />
          </TariffSectionCard>
        </Box>
      )}
    />
  );
};

export const TariffExpandableRow: FC<TariffExpandableRowProps> = ({
  type,
  icon,
  title,
  infoPopoverContent,
  details,
  summary,
  sections,
}) => {
  const { formatEuro } = useEuroFormatters();

  const resolvedSections: TariffExpandableSection[] = sections?.length
    ? sections
    : [{ type, icon, title: title ?? '', infoPopoverContent, details }];

  return (
    <TariffSectionList
      summary={summary}
      sections={resolvedSections}
      showSectionDivider={resolvedSections.length >= 2}
      stackGap="3"
      renderSection={section => (
        <TariffSectionCard
          icon={resolveTypeIcon(section.type, section.icon)}
          title={section.title}
          infoPopoverContent={section.infoPopoverContent}>
          <TariffExpandableDetails details={section.details} formatEuro={formatEuro} />
        </TariffSectionCard>
      )}
    />
  );
};
